// @ts-nocheck
/**
 * Press / trigger handler types for lifecycle events and component gestures.
 *
 * Triggers may invoke either a Blockly script (by scriptId) or a named
 * TemplateActionDefinition (by actionId). Legacy payloads omit `kind`:
 *   - `{ scriptId }` on event bindings
 *   - `{ actionId }` on component press (former ComponentActionRef)
 */

import type { JsonValue } from './primitives';

/** Discriminated handler targeting a document script definition. */
export interface ScriptEventHandler {
  kind: 'script';
  scriptId: string;
  actionId?: undefined;
  animationId?: undefined;
  args?: Record<string, JsonValue>;
}

/** Discriminated handler targeting an element-owned action definition. */
export interface ActionEventHandler {
  kind: 'action';
  actionId: string;
  scriptId?: undefined;
  animationId?: undefined;
  args?: Record<string, JsonValue>;
}

/** Discriminated handler targeting a template animation definition by id. */
export interface AnimationEventHandler {
  kind: 'animation';
  animationId: string;
  scriptId?: undefined;
  actionId?: undefined;
}

/** Canonical handler union for triggers and component press. */
export type EventHandler = ScriptEventHandler | ActionEventHandler | AnimationEventHandler;

/**
 * Legacy script reference (no `kind` field).
 * Accepted on event bindings and component press.
 */
export interface LegacyScriptInvocationShape {
  scriptId: string;
  actionId?: undefined;
  args?: Record<string, JsonValue>;
}

/**
 * Legacy action reference (no `kind` field).
 * Former shape of ComponentActionRef on button press / interactions.
 */
export interface LegacyActionInvocationShape {
  actionId: string;
  scriptId?: undefined;
  args?: Record<string, JsonValue>;
}

/** Handler on ScriptEventBinding.events[].handler */
export type ScriptEventHandlerInput = EventHandler | LegacyScriptInvocationShape;

/** Handler on button.action, interactions.onPress, interactions.onLongPress */
export type PressHandlerInput = EventHandler | LegacyScriptInvocationShape | LegacyActionInvocationShape;

export function isEventHandler(value: PressHandlerInput | ScriptEventHandlerInput): value is EventHandler {
  return (
    ('kind' in value && value.kind === 'script') ||
    ('kind' in value && value.kind === 'action') ||
    ('kind' in value && value.kind === 'animation')
  );
}

/**
 * Normalizes handler input to a discriminated EventHandler, or null if invalid.
 */
export function normalizeEventHandler(
  handler: ScriptEventHandlerInput | PressHandlerInput | undefined | null,
): EventHandler | null {
  if (handler == null) {
    return null;
  }
  if ('kind' in handler && handler.kind === 'script' && typeof handler.scriptId === 'string' && handler.scriptId.length > 0) {
    return {
      kind: 'script',
      scriptId: handler.scriptId,
      ...(handler.args !== undefined ? { args: handler.args } : {}),
    };
  }
  if ('kind' in handler && handler.kind === 'action' && typeof handler.actionId === 'string' && handler.actionId.length > 0) {
    return {
      kind: 'action',
      actionId: handler.actionId,
      ...(handler.args !== undefined ? { args: handler.args } : {}),
    };
  }
  if ('kind' in handler && handler.kind === 'animation' && typeof handler.animationId === 'string' && handler.animationId.length > 0) {
    return {
      kind: 'animation',
      animationId: handler.animationId,
    };
  }
  if (!('kind' in handler)) {
    const legacyScript = handler as LegacyScriptInvocationShape;
    if (typeof legacyScript.scriptId === 'string' && legacyScript.scriptId.length > 0) {
      return {
        kind: 'script',
        scriptId: legacyScript.scriptId,
        ...(legacyScript.args !== undefined ? { args: legacyScript.args } : {}),
      };
    }
    const legacyAction = handler as LegacyActionInvocationShape;
    if (typeof legacyAction.actionId === 'string' && legacyAction.actionId.length > 0) {
      return {
        kind: 'action',
        actionId: legacyAction.actionId,
        ...(legacyAction.args !== undefined ? { args: legacyAction.args } : {}),
      };
    }
  }
  return null;
}

/** Alias for component-press normalization. */
export const normalizePressHandler = normalizeEventHandler;
